import { useEffect, useMemo, useState } from "react";
import { PackagePlus } from "lucide-react";

import { Button } from "../../../../../components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../../../components/dialog";
import { FormErrorText, RequiredLabel } from "../../../../../components/form";
import { Input } from "../../../../../components/input";
import type { MerchantCouponListUIState } from "../../data/merchantCouponList.data";

const RESTOCK_PRESETS = [5, 10, 25, 50] as const;

interface RestockMerchantCouponDialogProps {
  coupon: MerchantCouponListUIState | null;
  open: boolean;
  submitting: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (quantity: number) => Promise<void>;
}

export function RestockMerchantCouponDialog({
  coupon,
  open,
  submitting,
  onOpenChange,
  onConfirm,
}: RestockMerchantCouponDialogProps) {
  const [quantityInput, setQuantityInput] = useState("");
  const quantity = Number(quantityInput);
  const validationMessage = useMemo(() => {
    if (!quantityInput) return undefined;
    return Number.isInteger(quantity) && quantity > 0
      ? undefined
      : "Enter a whole number greater than 0.";
  }, [quantity, quantityInput]);

  useEffect(() => {
    if (open) setQuantityInput("");
  }, [open, coupon?.id]);

  if (!coupon) return null;

  const projectedInventory = coupon.remaining + (Number.isInteger(quantity) && quantity > 0 ? quantity : 0);
  const canSubmit = !validationMessage && Number.isInteger(quantity) && quantity > 0 && !submitting;

  const handleConfirm = async () => {
    if (!canSubmit) return;
    await onConfirm(quantity);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md gap-6 p-6">
        <DialogHeader className="text-left">
          <div className="mb-1 flex h-10 w-10 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
            <PackagePlus className="h-5 w-5" aria-hidden="true" />
          </div>
          <DialogTitle>Restock coupon</DialogTitle>
          <DialogDescription>
            Add inventory to <span className="font-medium text-foreground">{coupon.title}</span>.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Available now</p>
            <p className="mt-1 text-2xl font-semibold text-slate-900">{coupon.remaining}</p>
          </div>
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
            <p className="text-xs font-medium uppercase tracking-wide text-emerald-700">After restock</p>
            <p className="mt-1 text-2xl font-semibold text-emerald-800">{projectedInventory}</p>
          </div>
        </div>

        <div>
          <RequiredLabel htmlFor="merchant-coupon-restock-quantity" required>
            Quantity to add
          </RequiredLabel>
          <Input
            id="merchant-coupon-restock-quantity"
            type="number"
            min={1}
            step={1}
            inputMode="numeric"
            value={quantityInput}
            disabled={submitting}
            placeholder="Enter quantity"
            className="mt-2 h-11"
            onChange={(event) => setQuantityInput(event.target.value.replace(/^0+(?=\d)/, ""))}
          />
          <FormErrorText message={validationMessage} />
        </div>

        <div className="flex flex-wrap gap-2" aria-label="Quick add quantities">
          {RESTOCK_PRESETS.map((preset) => (
            <Button
              key={preset}
              type="button"
              size="sm"
              variant="outline"
              disabled={submitting}
              onClick={() => setQuantityInput(String(preset))}
            >
              +{preset}
            </Button>
          ))}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" disabled={submitting} onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" disabled={!canSubmit} onClick={() => void handleConfirm()}>
            {submitting ? "Restocking..." : "Confirm restock"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default RestockMerchantCouponDialog;
